import { AppSettingType, FeatureManagementType } from "@api/apiClient";
import { CustomGridTreeDataGroupingCell } from "@components/DataGrid/components/cell/CustomGridTreeDataGroupingCell";
import { GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD, GRID_VISIBILITY_COL_DEF } from "@components/DataGrid/components/colDef/GridVisibilityColDef";
import QuantificationGridHeader from "@components/DataGrid/components/headers/QuantificationGridHeader";
import { SingleClickEditableColDef } from "@components/DataGrid/hooks/useSingleClickEditing";
import { createSubSystemLookup, createSystemLookup, transformMetadataToSubSystemOptions, transformMetadataToSystemOptions } from "@features/analysis/utils/metadataEntityUtils";
import { parseMultiplier } from "@features/analysis/utils/multiplier";
import { validateModelRow } from "@features/analysis/utils/treeGridUtils";
import i18n from "@i18n/i18n";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import { Theme, Tooltip } from "@mui/material";
import { GRID_CHECKBOX_SELECTION_FIELD, GRID_TREE_DATA_GROUPING_FIELD, GridApi, GridColDef, GridGroupNode, GridRenderCellParams } from "@mui/x-data-grid-premium";
import AppStore from "@stores/AppStore";
import _ from "lodash";
import { MutableRefObject } from "react";
import { editIconStyle, gridCellBaseStyle, gridCellErrorStyle, gridCellNormalStyle } from "./modelGridStyles";
import { getDynamicColumns } from "./MuiModelGrid.dynamic-columns";
import { NodeTypes } from "@/types/LCAoM/TreeBuilder";

/**
 * Dynamically generates the column configuration for the Model Grid.
 *
 * This function dynamically generates the columns configuration based
 * on application feature flags and project settings.
 *
 * @param apiRef - MutableRefObject to the GridApi for grid operations
 * @param scopeMap - Map of scope IDs to scope objects for scope column options
 * @param appStore - Application store containing feature flags and project settings
 * @param theme - MUI theme object for styling
 * @returns Array of GridColDef objects representing all grid columns
 */
export const getColumns = (_apiRef: MutableRefObject<GridApi>, scopeMap: any, _appStore: AppStore, theme: Theme, co2MaterialTypeSpecKind: string | null): GridColDef[] => {
  const columns = getDefaultColumns(_apiRef, scopeMap, _appStore, theme);

  // If we find Co2MaterialTypeSpecs in Nodes then add dynamic columns based on the Kind value
  const dynamicColumns = getDynamicColumns(co2MaterialTypeSpecKind);
  if (dynamicColumns.length > 0) {
    const index = columns.findIndex((col) => col.field === "cadMaterial");
    columns.splice(index, 0, ...dynamicColumns);
  }

  console.log("Model Grid Columns:", columns);

  return columns;
};

const getDefaultColumns = (_apiRef: MutableRefObject<GridApi>, scopeMap: any, _appStore: AppStore, theme: Theme): GridColDef[] => {
  // Get metadata entities from app store
  const metadataEntities = _appStore.metadataEntities;

  // Transform metadata into dropdown options
  const systemOptions = transformMetadataToSystemOptions(metadataEntities);
  const subSystemOptions = transformMetadataToSubSystemOptions(metadataEntities);

  // Create lookup maps for better performance
  const systemLookup = createSystemLookup(metadataEntities);
  const subSystemLookup = createSubSystemLookup(metadataEntities);

  return [
    {
      ...GRID_VISIBILITY_COL_DEF,
      headerName: i18n.t("model-grid.columns.visibility"),
    },
    {
      field: "multiplierValue",
      headerName: i18n.t("model-grid.columns.multiplier"),
      editable: true,
      width: 150,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.multiplier")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => {
        const value = row?.data?.multiplierValue;
        return typeof value === "number" && !isNaN(value) && value > 0 ? value.toFixed(2) : row?.data?.multiplierInput;
      },
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const multiplierValue = parseMultiplier(rawInput)?.toFixed(2) ?? null;
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            multiplierInput: rawInput,
            multiplierValue,
          },
          parsedMultiplier: multiplierValue,
          multiplierInput: rawInput,
        };
      },
      renderCell: (params) => {
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";
        const rowData = _apiRef.current.getRow(params.id);
        const input = rowData?.data?.multiplierInput ?? "";
        const error = rowData?.data?.error ?? "";

        const parsed = parseMultiplier(input);
        const isValid = input === "" || parsed !== null;
        const multiplierValue = parsed?.toFixed(2) ?? input;

        let displayValue = multiplierValue;

        //  Only mark parent (*) if any child has a different numeric value
        if (params.row?.children?.length > 0 && parsed !== null && rowData?.data?.hasChildMultiplierOverride === true) {
          displayValue = `${multiplierValue} *`;
        }

        return (
          <Tooltip title={!isValid && !isEditing ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(isValid ? gridCellNormalStyle : gridCellErrorStyle),
              }}
            >
              <div>{displayValue}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "notes",
      headerName: i18n.t("model-grid.columns.notes"),
      editable: true,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.notes")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => row?.data?.notes || "",
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            notes: rawInput,
          },
          notes: rawInput,
        };
      },
      renderCell: (params) => {
        const field = params.field;
        const rowData = _apiRef.current.getRow(params.id);
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";

        const errors = validateModelRow(rowData);
        const error = errors[field];
        const value = rowData?.data?.[field];

        return (
          <Tooltip title={!isEditing && error ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(error ? gridCellErrorStyle : gridCellNormalStyle),
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "carbonImpactValue",
      headerName: i18n.t("model-grid.columns.carbon-impact"),
      editable: true,
      width: 150,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.carbon-impact")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => {
        const value = row?.data?.carbonImpactValue;
        return typeof value === "number" && !isNaN(value) && value > 0 ? value.toFixed(2) : row?.data?.carbonImpactInput;
      },
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const carbonImpactValue = parseMultiplier(rawInput)?.toFixed(2) ?? null;
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            carbonImpactInput: rawInput,
            carbonImpactValue,
          },
          parsedCarbonImpact: carbonImpactValue,
          carbonImpactInput: rawInput,
        };
      },
      renderCell: (params) => {
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";
        const rowData = _apiRef.current.getRow(params.id);
        const input = rowData?.data?.carbonImpactInput ?? "";
        const error = rowData?.data?.carbonError ?? "";

        const parsed = parseMultiplier(input);
        const isValid = input === "" || parsed !== null;
        const carbonImpactValue = parsed?.toFixed(2) ?? input;

        let displayValue = carbonImpactValue;

        //  Only mark parent (*) if any child has a different numeric value
        if (params.row?.children?.length > 0 && parsed !== null && rowData?.data?.hasChildCarbonImpactOverride === true) {
          displayValue = `${carbonImpactValue} *`;
        }

        return (
          <Tooltip title={!isValid && !isEditing ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(isValid ? gridCellNormalStyle : gridCellErrorStyle),
              }}
            >
              <div>{displayValue}</div>
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "carbonImpactNotes",
      headerName: i18n.t("model-grid.columns.carbon-impact-notes"),
      editable: true,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.carbon-impact-notes")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => row?.data?.carbonImpactNotes || "",
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            carbonImpactNotes: rawInput,
          },
          carbonImpactNotes: rawInput,
        };
      },
      renderCell: (params) => {
        const field = params.field;
        const rowData = _apiRef.current.getRow(params.id);
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";

        const errors = validateModelRow(rowData);
        const error = errors[field];
        const value = rowData?.data?.[field];

        return (
          <Tooltip title={!isEditing && error ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(error ? gridCellErrorStyle : gridCellNormalStyle),
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "systemId",
      headerName: i18n.t("model-grid.columns.system"),
      editable: true,
      type: "singleSelect",
      valueOptions: systemOptions,
      isSingleClickEditable: true,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.system")} />
          <Tooltip title="Editable dropdown" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_val, row) => row?.data?.systemId || "",
      valueFormatter: (value?: string) => {
        return systemLookup[value || ""] || "";
      },
    } as SingleClickEditableColDef,
    {
      field: "subSystemId",
      headerName: i18n.t("model-grid.columns.sub-system"),
      editable: true,
      type: "singleSelect",
      isSingleClickEditable: true,
      valueOptions: ({ row }) => {
        const systemId = row?.data?.systemId;
        return subSystemOptions.filter((s) => !systemId || s.systemId === systemId || s.value === "");
      },
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.sub-system")} />
          <Tooltip title="Editable dropdown" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_val, row) => row?.data?.subSystemId || "",
      valueSetter: (value, row) => {
        // Find the selected subsystem to get its parent systemId
        const selectedSubSystem = subSystemOptions.find((s) => s.value === value);
        const parentSystemId = selectedSubSystem?.systemId || "";

        // Update both subSystemId and systemId (if systemId is empty or doesn't match)
        const currentSystemId = row?.data?.systemId || "";
        const shouldUpdateSystem = !currentSystemId || (parentSystemId && currentSystemId !== parentSystemId);

        return {
          ...row,
          data: {
            ...row?.data,
            subSystemId: value || "",
            // Auto-update systemId if it's empty or doesn't match the subsystem's parent
            systemId: shouldUpdateSystem ? parentSystemId : currentSystemId,
          },
        };
      },
      valueFormatter: (value?: string) => {
        return subSystemLookup[value || ""] || "";
      },
    } as SingleClickEditableColDef,
    {
      field: "attribute1",
      headerName: i18n.t("model-grid.columns.attribute1"),
      editable: true,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.attribute1")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => row?.data?.attribute1 || "",
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            attribute1: rawInput,
          },
          attribute1: rawInput,
        };
      },
      renderCell: (params) => {
        const field = params.field;
        const rowData = _apiRef.current.getRow(params.id);
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";

        const errors = validateModelRow(rowData);
        const error = errors[field];
        const value = rowData?.data?.[field];

        return (
          <Tooltip title={!isEditing && error ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(error ? gridCellErrorStyle : gridCellNormalStyle),
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "attribute2",
      headerName: i18n.t("model-grid.columns.attribute2"),
      editable: true,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.attribute2")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => row?.data?.attribute2 || "",
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            attribute2: rawInput,
          },
          attribute2: rawInput,
        };
      },
      renderCell: (params) => {
        const field = params.field;
        const rowData = _apiRef.current.getRow(params.id);
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";

        const errors = validateModelRow(rowData);
        const error = errors[field];
        const value = rowData?.data?.[field];

        return (
          <Tooltip title={!isEditing && error ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(error ? gridCellErrorStyle : gridCellNormalStyle),
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "attribute3",
      headerName: i18n.t("model-grid.columns.attribute3"),
      editable: true,
      flex: 1,
      renderHeader: () => (
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <QuantificationGridHeader name={i18n.t("model-grid.columns.attribute3")} />
          <Tooltip title="Editable column" arrow>
            <EditIcon style={editIconStyle} />
          </Tooltip>
        </span>
      ),
      valueGetter: (_value: any, row: any) => row?.data?.attribute3 || "",
      preProcessEditCellProps: (params) => {
        const rawInput = (params.props.value ?? "").toString().trim();
        const row = _apiRef.current.getRow(params.id);
        return {
          ...row,
          data: {
            ...row?.data,
            attribute3: rawInput,
          },
          attribute3: rawInput,
        };
      },
      renderCell: (params) => {
        const field = params.field;
        const rowData = _apiRef.current.getRow(params.id);
        const isEditing = params.api.getCellMode(params.id, params.field) === "edit";

        const errors = validateModelRow(rowData);
        const error = errors[field];
        const value = rowData?.data?.[field];

        return (
          <Tooltip title={!isEditing && error ? error : ""} placement="top" arrow>
            <div
              style={{
                ...gridCellBaseStyle,
                ...(error ? gridCellErrorStyle : gridCellNormalStyle),
              }}
            >
              {value}
            </div>
          </Tooltip>
        );
      },
    },
    {
      field: "quantityTotal",
      headerName: i18n.t("model-grid.columns.quantity-total"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.quantity-total")} />,
      flex: 1,
      type: "number",
      valueGetter: (_value: any, row: any) => row?.data?.originalQuantity ?? row?.data?.quantityTotal,
      valueFormatter: (value?: number) => {
        return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
      },
    },
    {
      field: "lengthTotal",
      headerName: i18n.t("quantification-header", {
        name: i18n.t("model-grid.columns.length-total"),
        unit: i18n.t("units.m"),
      }),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.length-total")} unit={i18n.t("units.m")} />,
      flex: 1,
      type: "number",
      valueGetter: (_value: any, row: any) => {
        // When An element contains insulation thickness as a child component
        // length should not be considered
        if (row.data?.cadName?.trim().includes("Insulation") && (row.data?.originalLength ?? row.data?.lengthTotal) == 0) {
          return;
        }
        return row?.data?.originalLength ?? row?.data?.lengthTotal;
      },
      valueFormatter: (value: any) => {
        // alert(value)
        if (_.isFinite(value)) {
          return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
        } else {
          return "-";
        }
      },
    },
    {
      field: "areaTotal",
      headerName: i18n.t("quantification-header", {
        name: i18n.t("model-grid.columns.area-total"),
        unit: i18n.t("units.m2"),
      }),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.area-total")} unit={i18n.t("units.m2")} />,
      flex: 1,
      type: "number",

      valueGetter: (_value: any, row: any) => {
        // When An element contains insulation thickness as a child component
        // area should not be considered
        if (row.data?.cadName?.trim().includes("Insulation") && (row.data?.originalArea ?? row.data?.areaTotal) == 0) {
          return;
        }
        return row?.data?.originalArea ?? row?.data?.areaTotal;
      },
      valueFormatter: (value: any) => {
        // alert(value)
        if (_.isFinite(value)) {
          return i18n.t("number", { value, digits: AppSettingType.NumberFormat_EC_Digits });
        } else {
          return "-";
        }
      },
    },
    {
      field: "volumeTotal",
      headerName: i18n.t("quantification-header", {
        name: i18n.t("model-grid.columns.volume-total"),
        unit: i18n.t("units.m3"),
      }),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.volume-total")} unit={i18n.t("units.m3")} />,
      flex: 1,
      type: "number",
      valueGetter: (_value: any, row: any) => row?.data?.originalVolume ?? row?.data?.volumeTotal,
      valueFormatter: (value?: number) => {
        return i18n.t("number", {
          value,
          digits: AppSettingType.NumberFormat_EC_Digits,
        });
      },
      renderCell: (params) => {
        const value = params.value;
        const row = params.row;
        const parameters = row?.data?.modelParameters || [];

        const formattedValue = i18n.t("number", {
          value,
          digits: AppSettingType.NumberFormat_EC_Digits,
        });

        const isFooterRow =
          params.rowNode?.type === "footer" ||
          String(params.id).startsWith("auto-generated-group-footer-");

        if (isFooterRow) {
          return (
            <span style={{ color: theme.palette.primary.dark }}>
              {formattedValue}
            </span>
          );
        }

        // Categorize parameters
        const materialParams = parameters.filter(
          (p: any) => p?.name && p?.value && (p.name.includes("Material") || p.name.includes("Thickness") || p.name.includes("DIAM")) && !p.name.includes("Insulation"),
        );
        const insulationParams = parameters.filter((p: any) => p?.name && p?.value && p.name.includes("Insulation"));
        const factorParams = parameters.filter((p: any) => p?.name && p?.value && (p.name.includes("factor") || p.name.includes("Factor")));
        const otherParams = parameters.filter(
          (p: any) =>
            p?.name &&
            p?.value &&
            !p.name.includes("Material") &&
            !p.name.includes("Thickness") &&
            !p.name.includes("DIAM") &&
            !p.name.includes("Insulation") &&
            !p.name.includes("factor") &&
            !p.name.includes("Factor"),
        );

        const hasAnyParams = materialParams.length > 0 || insulationParams.length > 0 || factorParams.length > 0 || otherParams.length > 0;

        const cellContent = (
          <div
            style={{
              backgroundColor: hasAnyParams ? "#e0f7e9" : "transparent",
              padding: "4px 8px",
              borderRadius: "4px",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {formattedValue}
          </div>
        );

        if (!hasAnyParams) {
          return cellContent;
        }

        // Build categorized tooltip content
        const tooltipSections = [];

        // Check if element is a pipe, duct, conduit, or cable tray based on category or parameters
        const categoryName = row?.data?.cadModelCategory?.name || row?.data?.cadModelCategory?.value || "";
        const isPipeOrConduit = categoryName && (categoryName.includes("Pipe") || categoryName.includes("Pipes") || categoryName.includes("Conduit"));

        // Default thickness values in millimeters (from backend configuration)
        const defaultThicknessMap: Record<string, number> = {
          OST_PipeCurves: 1.4,
          OST_PipeFitting: 1.4,
          OST_DuctCurves: 1.214,
          OST_DuctFitting: 1.214,
          OST_Conduit: 4.7,
          OST_ConduitFitting: 4.7,
          OST_CableTray: 1.214,
          OST_CableTrayFitting: 1.214,
        };

        // Calculate thickness for pipes, ducts, conduits
        const calculateThickness = (params: any[], allParams: any[]) => {
          if (!isPipeOrConduit) {
            return { hasThickness: false };
          }

          // Priority 1: Check for Material_Thickness parameter (from ThicknessParamMap)
          // This parameter value is stored in meters (converted from internal units)
          const materialThicknessParam = allParams.find(
            (p: any) => p?.name && (p.name === "Asset_Element_Material_Thickness" || p.name.includes("Material_Thickness") || p.name.includes("Material Thickness")),
          );

          if (materialThicknessParam) {
            const thicknessInMeters = parseFloat(materialThicknessParam.value);
            if (!isNaN(thicknessInMeters) && thicknessInMeters > 0) {
              return {
                hasThickness: true,
                thicknessInMeters: thicknessInMeters,
                thicknessInMm: thicknessInMeters * 1000, // Convert to mm for display
                outerDiamParam: null,
                innerDiamParam: null,
                thicknessSource: "material_parameter",
              };
            }
          }

          // Priority 2: Calculate from diameters if both are available (for pipes/conduits)
          const outerDiamParam = params.find(
            (p: any) =>
              p?.name &&
              (p.name.includes("OUTER_DIAM") ||
                p.name.includes("RBS_PIPE_OUTER_DIAMETER") ||
                p.name.includes("Outer Diameter") ||
                p.name.includes("Outside Diameter") ||
                p.name.includes("OuterDiameter") ||
                p.name.toLowerCase().includes("outer diameter")),
          );
          const innerDiamParam = params.find(
            (p: any) =>
              p?.name &&
              (p.name.includes("INNER_DIAM") ||
                p.name.includes("RBS_PIPE_INNER_DIAMETER") ||
                p.name.includes("Inner Diameter") ||
                p.name.includes("Inside Diameter") ||
                p.name.includes("InnerDiameter") ||
                p.name.toLowerCase().includes("inner diameter") ||
                p.name.toLowerCase().includes("inside diameter")),
          );

          if (outerDiamParam && innerDiamParam) {
            const outerDiam = parseFloat(outerDiamParam.value);
            const innerDiam = parseFloat(innerDiamParam.value);

            // Diameters are stored in meters
            if (!isNaN(outerDiam) && !isNaN(innerDiam) && outerDiam > innerDiam) {
              const thicknessInMeters = (outerDiam - innerDiam) / 2;
              return {
                hasThickness: true,
                thicknessInMeters: thicknessInMeters,
                thicknessInMm: thicknessInMeters * 1000, // Convert to mm for display
                outerDiamParam: outerDiamParam,
                innerDiamParam: innerDiamParam,
                thicknessSource: "calculated",
              };
            }
          }

          // Priority 3: Use default thickness parameter if available (value is in mm)
          const defaultThicknessParam = allParams.find((p: any) => p?.name && p.name.includes("DefaultThickness"));

          if (defaultThicknessParam) {
            const defaultThicknessMm = parseFloat(defaultThicknessParam.value);
            if (!isNaN(defaultThicknessMm) && defaultThicknessMm > 0) {
              return {
                hasThickness: true,
                thicknessInMeters: defaultThicknessMm / 1000, // Convert mm to meters
                thicknessInMm: defaultThicknessMm,
                outerDiamParam: outerDiamParam,
                innerDiamParam: innerDiamParam,
                thicknessSource: "default_parameter",
              };
            }
          }

          // Priority 4: Fallback to category-based default thickness
          const categoryKey = categoryName || "";
          const defaultThicknessMm = defaultThicknessMap[categoryKey];
          if (defaultThicknessMm && defaultThicknessMm > 0) {
            return {
              hasThickness: true,
              thicknessInMeters: defaultThicknessMm / 1000, // Convert mm to meters
              thicknessInMm: defaultThicknessMm,
              outerDiamParam: outerDiamParam,
              innerDiamParam: innerDiamParam,
              thicknessSource: "default_category",
            };
          }

          return { hasThickness: false };
        };

        const thicknessInfo = calculateThickness(materialParams, parameters);

        if (materialParams.length > 0) {
          tooltipSections.push("Material Parameters:");

          // Get diameter parameters for special handling
          const outerDiamParam = materialParams.find(
            (p: any) =>
              p?.name &&
              (p.name.includes("OUTER_DIAM") ||
                p.name.includes("RBS_PIPE_OUTER_DIAMETER") ||
                p.name.includes("Outer Diameter") ||
                p.name.includes("Outside Diameter") ||
                p.name.includes("OuterDiameter") ||
                p.name.toLowerCase().includes("outer diameter")),
          );
          const innerDiamParam = materialParams.find(
            (p: any) =>
              p?.name &&
              (p.name.includes("INNER_DIAM") ||
                p.name.includes("RBS_PIPE_INNER_DIAMETER") ||
                p.name.includes("Inner Diameter") ||
                p.name.includes("Inside Diameter") ||
                p.name.includes("InnerDiameter") ||
                p.name.toLowerCase().includes("inner diameter") ||
                p.name.toLowerCase().includes("inside diameter")),
          );

          // Get default thickness for this category (in mm)
          const categoryKey = categoryName || "";
          const defaultThicknessMm = defaultThicknessMap[categoryKey] || 1.4; // Default fallback

          // Process material parameters with diameter-specific logic
          materialParams.forEach((p: any) => {
            const isOuterDiam = p === outerDiamParam;
            const isInnerDiam = p === innerDiamParam;

            // Display the parameter
            tooltipSections.push(`  ${p.name}: ${p.value * 1000} mm`);

            // Add thickness information based on available diameters
            if (isOuterDiam || isInnerDiam) {
              if (outerDiamParam && innerDiamParam && isInnerDiam) {
                // Both diameters present - show effective thickness after inner diameter
                const outerDiam = parseFloat(outerDiamParam.value);
                const innerDiam = parseFloat(innerDiamParam.value);

                if (!isNaN(outerDiam) && !isNaN(innerDiam) && outerDiam > innerDiam) {
                  const effectiveThickness = ((outerDiam - innerDiam) / 2) * 1000; // Convert to mm
                  const formattedThickness = i18n.t("number", {
                    value: effectiveThickness,
                    digits: AppSettingType.NumberFormat_EC_Digits,
                  });
                  tooltipSections.push(`  Effective Thickness: ${formattedThickness} mm`);
                }
              } else if (outerDiamParam && !innerDiamParam && isOuterDiam) {
                // Only outer diameter - show default thickness
                const formattedThickness = i18n.t("number", {
                  value: defaultThicknessMm,
                  digits: AppSettingType.NumberFormat_EC_Digits,
                });
                tooltipSections.push(`  Default Thickness: ${formattedThickness} mm`);
              } else if (!outerDiamParam && innerDiamParam && isInnerDiam) {
                // Only inner diameter - show default thickness
                const formattedThickness = i18n.t("number", {
                  value: defaultThicknessMm,
                  digits: AppSettingType.NumberFormat_EC_Digits,
                });
                tooltipSections.push(`  Default Thickness: ${formattedThickness} mm`);
              }
            }
          });
        }

        if (insulationParams.length > 0) {
          if (tooltipSections.length > 0) tooltipSections.push("");
          tooltipSections.push("Insulation Parameters:", ...insulationParams.map((p: any) => `  ${p.name}: ${p.value * 1000} mm`));
        }

        if (factorParams.length > 0) {
          if (tooltipSections.length > 0) tooltipSections.push("");
          tooltipSections.push(" Factor Parameters:", ...factorParams.map((p: any) => `  ${p.name}: ${p.value}`));
        }

        if (otherParams.length > 0) {
          if (tooltipSections.length > 0) tooltipSections.push("");
          tooltipSections.push("Other Parameters:", ...otherParams.map((p: any) => `  ${p.name}: ${p.value}`));
        }

        return (
          <Tooltip
            title={
              <div style={{ maxWidth: "400px", color: "#FFFFFF" }}>
                <div style={{ fontWeight: "bold", marginBottom: "8px", color: "#FFFFFF" }}>
                  Parameters for {row?.data?.cadName || "Element"} ({materialParams.length + insulationParams.length + factorParams.length + otherParams.length})
                </div>
                <pre style={{ margin: 0, whiteSpace: "pre-wrap", color: "#FFFFFF", fontFamily: "inherit" }}>{tooltipSections.join("\n")}</pre>
              </div>
            }
            arrow
            componentsProps={{
              tooltip: {
                sx: {
                  backgroundColor: "rgba(97, 97, 97, 0.95)",
                  color: "#FFFFFF",
                  fontSize: "12px",
                  padding: "8px 12px",
                  maxWidth: "400px",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(97, 97, 97, 0.95)",
                },
              },
            }}
          >
            {cellContent}
          </Tooltip>
        );
      },
    },
    {
      field: "cadMaterial",
      headerName: i18n.t("model-grid.columns.cad-material"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.cad-material")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => {
        return row?.data?.cadMaterial;
      },
    },
    {
      field: "isStructuralMaterial",
      headerName: i18n.t("model-grid.columns.is-structural-material"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.is-structural-material")} />,
      flex: 1,
      type: "boolean",
      renderCell: (params: GridRenderCellParams) => {
        return params.row.data?.isStructuralMaterial ? <CheckIcon sx={{ color: theme.palette.success.main }} /> : null;
      },
    },
    {
      field: "siblings",
      headerName: i18n.t("model-grid.columns.siblings"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.siblings")} />,
      flex: 1,
      type: "number",
      valueGetter: (_value: any, row: any) => {
        if (row.data?.cadName?.trim().includes("Insulation") && (row.data?.originalLength ?? row.data?.lengthTotal) == 0) {
          return;
        }
        return row?.data?.siblings;
      },
      valueFormatter: (value?: number) => {
        if (value != undefined && _.isFinite(value)) {
          return i18n.t("number", { value, maximumFractionDigits: 0 });
        } else {
          return "";
        }
      },
    },
    {
      field: "type",
      headerName: i18n.t("model-grid.columns.type"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.type")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => {
       if (row?.type === "group" && row?.data?.nodeType === NodeTypes.Type) {
       return row?.label || "";
       }
         return row?.data?.cadName || "";
      },
    },
    {
      field: "key",
      headerName: i18n.t("model-grid.columns.key"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.key")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => {
        return row?.key;
      },
    },
    {
      field: "cadId",
      headerName: i18n.t("model-grid.columns.cad-id"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.cad-id")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => row?.data?.cadId || "",
    },
    {
      field: "cadModelCategory",
      headerName: i18n.t("model-grid.columns.cad-model-category-name"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.cad-model-category-name")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => {
        const categoryId = row?.data?.cadModelCategoryId;
        const label = _appStore.getCadModelCategoryLabel(categoryId);
        return label || "";
      },
    },
    {
      field: "cadFamily",
      headerName: i18n.t("model-grid.columns.cad-family"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.cad-family")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => {
        if (row?.type === "group" && row?.data?.nodeType === NodeTypes.Family) {
        return row?.label || "";
      }
      return row?.data?.cadFamily || "";

      },
    },
    {
      field: "cadName",
      headerName: i18n.t("model-grid.columns.cad-name"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.cad-name")} />,
      flex: 1,
      valueGetter: (_value: any, row: any) => {
        return row?.data?.cadName;
      },
    },
    {
      field: "scopeId",
      headerName: i18n.t("model-grid.columns.scope"),
      renderHeader: () => <QuantificationGridHeader name={i18n.t("model-grid.columns.scope")} />,
      flex: 1,
      type: "singleSelect",
      valueGetter: (_value: any, row: any) => {
        return row.data?.scopeId?.toString() || "";
      },
      valueFormatter: (value?: string) => {
        return value ? scopeMap[value]?.name : "";
      },
      valueOptions: scopeMap
        ? [
            ...Object.entries(scopeMap).map(([key, value]: [string, any]) => ({
              value: key,
              label: value.name,
            })),
          ]
        : [],
    },
  ];
};

export const getGroupingColumnDef = (headerName: string): GridColDef => {
  return {
    field: "name",
    headerName: headerName,
    renderHeader: () => <QuantificationGridHeader name={headerName} />,
    filterable: true,
    hideable: false,
    valueGetter: (_value: any, row: any) => row?.data?.label || "",
    renderCell: (params) => {
      return <CustomGridTreeDataGroupingCell {...(params as GridRenderCellParams<any, any, any, GridGroupNode>)} hideDescendantCount={true} />;
    },
  };
};

export const getFilteredColumns = (
  columns: GridColDef[],
  appStore: AppStore,
): {
  filteredColumns: GridColDef[];
  columnVisibilityModel: Record<string, boolean>;
} => {
  let filteredColumns = [...columns];
  const defaultVisibleColumns = [
    GRID_CHECKBOX_SELECTION_FIELD,
    GRID_TREE_DATA_GROUPING_FIELD,

    "label",
    GRID_CHECKBOX_VISIBILITY_SELECTION_FIELD,
    "multiplierInput",
    "multiplierValue",
    "notes",
    "carbonImpactInput",
    "carbonImpactValue",
    "carbonImpactNotes",
    "systemId",
    "subSystemId",
    "attribute1",
    "attribute2",
    "attribute3",
    "quantityTotal",
    "lengthTotal",
    "areaTotal",
    "volumeTotal",
    "systemType",
    "cadMaterial",
    // NOTE: Co2MaterialType columns inserted here
  ];
  const scopeColumns = ["scopeId", "scoped"];
  const systemSubSystemColumns = ["systemId", "subSystemId"];

  // Filter out system and subsystem columns if AttributesVisibility feature is not enabled
  if (!appStore.hasFeature(FeatureManagementType.AttributesVisibility)) {
    filteredColumns = filteredColumns.filter((c) => !systemSubSystemColumns.includes(c.field));
  }

  if (!appStore.hasFeature(FeatureManagementType.ModelScopes)) {
    filteredColumns = filteredColumns.filter((c) => !scopeColumns.includes(c.field));
  }

  // Generate column visibility model
  const columnVisibilityModel = filteredColumns.reduce<Record<string, boolean>>((acc, col) => {
    acc[col.field] = defaultVisibleColumns.includes(col.field); // Set true for visibleColumns, false for others
    return acc;
  }, {});

  //console.log(filteredColumns);
  //console.log(columnVisibilityModel);

  return { filteredColumns, columnVisibilityModel };
};
